import { resolveDefaultCriteria, withAvailableDirectFlight, type SearchCriteria } from '@/entities/searchCriteria';
import { deriveDateFrom, getAnchor, normalizeDateFrom } from '@/features/searchForm/dateFrom';
import type { DateRange, DepartureCity, SearchFormData } from '@/shared/model';
import { withAvailableEmirates } from './emiratesFilter';
import { ALTERNATIVE_DEPARTURE_CITY_IDS, ALTERNATIVE_SEARCH_FLEXIBLE_DAYS } from '../config/alternativeSearch';

interface AlternativeDepartureCitiesExclusions {
  userCityId: number;
  currentCityId: number;
  emptyCityIds: readonly number[];
}

export function isInsideDateRange(date: string, range: DateRange): boolean {
  return range.from <= date && date <= range.to;
}

export function isDateRangeInside(inner: DateRange, outer: DateRange): boolean {
  return isInsideDateRange(inner.from, outer) && isInsideDateRange(inner.to, outer);
}

export function withDepartureCity(criteria: SearchCriteria, city: DepartureCity): SearchCriteria {
  return withAvailableEmirates(withAvailableDirectFlight({ ...criteria, fromCity: city }));
}

export function withFlexibleDates(
  criteria: SearchCriteria,
  days: number = ALTERNATIVE_SEARCH_FLEXIBLE_DAYS,
): SearchCriteria | null {
  if (!criteria.dateRange) return null;

  const dateRange = deriveDateFrom(getAnchor(criteria.dateRange), days);
  if (isDateRangeInside(dateRange, criteria.dateRange)) return null;

  return withAvailableDirectFlight({ ...criteria, dateRange });
}

export function withCheckInDate(criteria: SearchCriteria, date: string): SearchCriteria {
  return withAvailableDirectFlight({ ...criteria, dateRange: { from: date, to: date, daysFlexible: 0 } });
}

export function buildDefaultCriteria(criteria: SearchCriteria, formData: SearchFormData): SearchCriteria | null {
  const { destination, fromCity, tourists } = criteria;
  if (!destination || destination.kind === 'none') return null;

  const defaults = resolveDefaultCriteria(formData.defaultCriteria, destination.country.id, fromCity?.type);
  const dateRange = defaults.dateRange ?? criteria.dateRange;

  return {
    fromCity,
    tourists,
    destination: { kind: 'country', country: destination.country },
    nights: defaults.nights ?? criteria.nights,
    dateRange: dateRange && normalizeDateFrom(dateRange),
  };
}

export function getAlternativeDepartureCities(
  cities: readonly DepartureCity[],
  { userCityId, currentCityId, emptyCityIds }: AlternativeDepartureCitiesExclusions,
): DepartureCity[] {
  const excludedIds = new Set<number>([userCityId, currentCityId, ...emptyCityIds]);

  return ALTERNATIVE_DEPARTURE_CITY_IDS.filter((id) => !excludedIds.has(id)).flatMap((id) => {
    const city = cities.find((candidate) => candidate.id === id);

    return city ? [city] : [];
  });
}
